import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

export interface UseFreakReturn {
  toggleFreak: () => void;
  isFreak: boolean;
  freakActivating: boolean;
}

const FreakContext = createContext<UseFreakReturn | undefined>(undefined);

export const FreakProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [isFreak, setIsFreak] = useState<boolean>(() => {
    // Load the initial state from localStorage, default to false if not set
    const savedState = localStorage.getItem("isToggled");
    return savedState === "true"; // Convert string to boolean
  });
  const [freakActivating, setFreakActivating] = useState<boolean>(false);

  // Ref to store the timeout ID
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Clear the pending "activating" timeout if the provider unmounts mid-animation.
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  const toggleFreak = useCallback(
    () =>
      setIsFreak((prev) => {
        const newState = !prev;
        localStorage.setItem("isToggled", newState.toString()); // Save to localStorage
        setFreakActivating(!prev);

        // Clear the previous timeout if it exists
        if (timeoutRef.current) {
          clearTimeout(timeoutRef.current);
        }

        // Set a new timeout
        timeoutRef.current = setTimeout(() => {
          setFreakActivating(false);
        }, 6000);

        return newState;
      }),
    []
  );

  const value = useMemo<UseFreakReturn>(
    () => ({
      isFreak,
      toggleFreak,
      freakActivating,
    }),
    [isFreak, toggleFreak, freakActivating]
  );

  return (
    <FreakContext.Provider value={value}>{children}</FreakContext.Provider>
  );
};

// Custom hook to use the coin context
export const useFreakContext = (): UseFreakReturn => {
  const context = useContext(FreakContext);
  if (context === undefined) {
    throw new Error("No Freaks in Here!");
  }
  return context;
};
